import React from 'react'
import { Row, Col, Typography, Flex, Divider, Spin } from 'antd'
import { CardHeader } from '@/shared/ui/layout/styled'
import { Icon } from '@/shared/ui/Icon/Icon'
import { TransferKey } from 'antd/es/transfer/interface'
import { TransferSection } from './ui/TransferSection'
import { useBreakpoint } from '@/app/hooks/useBreakpoint'
import {
  TRANSFER_MODES,
  TransferMode,
} from '@/pages/transfer/model/transferTypes'
import { SYSTEM_TYPE, useSystemStore } from '@/app/store/systemStore'

const { Title } = Typography

export interface DataTransferCardProps {
  transferMode: TransferMode
  // Для режима "Только сущности"
  objectsTargetKeys: TransferKey[]
  usersTargetKeys: TransferKey[]
  geozonesTargetKeys: TransferKey[]
  driversTargetKeys: TransferKey[]
  retranslationsTargetKeys: TransferKey[]
  loadMessages: boolean
  loadDriversCodes: boolean
  messagePeriod: number
  // Для режима "Полный перенос"
  accountsTargetKeys: TransferKey[]
  loadMessagesFull: boolean
  messagePeriodFull: number
  // Обработчики для режима "Только сущности"
  onObjectsChange: (targetKeys: TransferKey[]) => void
  onUsersChange: (targetKeys: TransferKey[]) => void
  onGeozonesChange: (targetKeys: TransferKey[]) => void
  onDriversChange: (targetKeys: TransferKey[]) => void
  onSelectAllObjects: () => void
  onDeselectAllObjects: () => void
  onSelectAllUsers: () => void
  onDeselectAllUsers: () => void
  onSelectAllGeozones: () => void
  onDeselectAllGeozones: () => void
  onSelectAllDrivers: () => void
  onDeselectAllDrivers: () => void
  onLoadMessagesChange: (checked: boolean) => void
  onLoadDriversCodesChange: (checked: boolean) => void
  onMessagePeriodChange: (period: number) => void
  onRetranslationsChange: (targetKeys: TransferKey[]) => void
  onSelectAllRetranslations: () => void
  onDeselectAllRetranslations: () => void
  // Обработчики для режима "Полный перенос"
  onAccountsChange: (targetKeys: TransferKey[]) => void
  onSelectAllAccounts: () => void
  onDeselectAllAccounts: () => void
  onLoadMessagesFullChange: (checked: boolean) => void
  onMessagePeriodFullChange: (period: number) => void
  // данные
  objectsData: any[]
  usersData: any[]
  geozonesData: any[]
  driversData: any[]
  retranslationsData: any[]
  accountsData: any[]
  // Кнопка запуска переноса
  isLoading?: boolean
}

export const DataTransferCard: React.FC<DataTransferCardProps> = ({
  transferMode,
  // Для режима "Только сущности"
  objectsTargetKeys,
  usersTargetKeys,
  geozonesTargetKeys,
  driversTargetKeys,
  retranslationsTargetKeys,
  loadMessages,
  loadDriversCodes,
  messagePeriod,
  // Для режима "Полный перенос"
  accountsTargetKeys,
  loadMessagesFull,
  messagePeriodFull,
  // Обработчики для режима "Только сущности"
  onObjectsChange,
  onUsersChange,
  onGeozonesChange,
  onDriversChange,
  onRetranslationsChange,
  onSelectAllObjects,
  onDeselectAllObjects,
  onSelectAllUsers,
  onDeselectAllUsers,
  onSelectAllGeozones,
  onDeselectAllGeozones,
  onSelectAllDrivers,
  onDeselectAllDrivers,
  onSelectAllRetranslations,
  onDeselectAllRetranslations,
  onLoadMessagesChange,
  onLoadDriversCodesChange,
  onMessagePeriodChange,
  // Обработчики для режима "Полный перенос"
  onAccountsChange,
  onSelectAllAccounts,
  onDeselectAllAccounts,
  onLoadMessagesFullChange,
  onMessagePeriodFullChange,
  // Mock данные
  objectsData,
  usersData,
  geozonesData,
  driversData,
  retranslationsData,
  accountsData,
  isLoading,
}) => {
  const { current: breakpoint, isSm, isXs, isMd, isLg } = useBreakpoint()
  const { selectedSystem } = useSystemStore()
  const isOmnicomm = selectedSystem === SYSTEM_TYPE.OMNICOMM

  const getGridSpan = () => {
    if (breakpoint === 'xs' || breakpoint === 'sm') return 24
    if (breakpoint === 'lg' || breakpoint === 'md') return 24
    return 12
  }

  const gridSpan = getGridSpan()

  const isTablet = isXs || isSm || isMd || isLg
  const isMobile = isXs || isSm

  return (
    <>
      <CardHeader gap={8}>
        <Icon name="transfer_title" size={32} />
        <Title level={2}>Выбор данных для переноса</Title>
      </CardHeader>
      <Spin spinning={isLoading ?? false}>
        <Flex vertical gap={isMobile ? 24 : 32}>
          {transferMode === TRANSFER_MODES.ENTITIES ? (
            <>
              <Row gutter={32} style={{ gap: isTablet ? '24px' : 0 }}>
                <Col span={gridSpan}>
                  <TransferSection
                    title="Объекты"
                    dataSource={objectsData}
                    value={objectsTargetKeys}
                    onChange={onObjectsChange}
                    onSelectAll={onSelectAllObjects}
                    onDeselectAll={onDeselectAllObjects}
                    showMessagesBlock
                    defaultOpen
                    loadMessages={loadMessages}
                    onLoadMessagesChange={onLoadMessagesChange}
                    messagePeriod={messagePeriod}
                    onMessagePeriodChange={onMessagePeriodChange}
                  />
                </Col>
                {isMobile && (
                  <Divider style={{ minWidth: 'auto', margin: '0 16px' }} />
                )}
                <Col span={gridSpan}>
                  <TransferSection
                    title="Пользователи"
                    height={!isMobile ? 432 : 354}
                    dataSource={usersData}
                    value={usersTargetKeys}
                    onChange={onUsersChange}
                    onSelectAll={onSelectAllUsers}
                    onDeselectAll={onDeselectAllUsers}
                    defaultOpen={false}
                  />
                </Col>
              </Row>
              {isMobile && <Divider />}
              <Row gutter={32} style={{ gap: isTablet ? '24px' : 0 }}>
                <Col span={gridSpan}>
                  <TransferSection
                    height={!isMobile ? 458 : 354}
                    title="Геозоны"
                    dataSource={geozonesData}
                    value={geozonesTargetKeys}
                    onChange={onGeozonesChange}
                    onSelectAll={onSelectAllGeozones}
                    onDeselectAll={onDeselectAllGeozones}
                    defaultOpen={false}
                  />
                </Col>
                {isMobile && (
                  <Divider style={{ minWidth: 'auto', margin: '0 16px' }} />
                )}
                <Col span={gridSpan}>
                  <TransferSection
                    title="Водители"
                    height={!isMobile ? 408 : 354}
                    dataSource={driversData}
                    value={driversTargetKeys}
                    onChange={onDriversChange}
                    onSelectAll={onSelectAllDrivers}
                    onDeselectAll={onDeselectAllDrivers}
                    defaultOpen={false}
                    showDriversBlock
                    loadDriversCodes={loadDriversCodes}
                    onLoadDriversCodesChange={onLoadDriversCodesChange}
                  />
                </Col>
              </Row>
              {!isOmnicomm && (
                <>
                  {isMobile && <Divider />}
                  <Row gutter={32} style={{ gap: isTablet ? '24px' : 0 }}>
                    <Col span={gridSpan}>
                      <TransferSection
                        height={!isMobile ? 408 : 354}
                        title="Ретрансляторы"
                        dataSource={retranslationsData}
                        value={retranslationsTargetKeys}
                        onChange={onRetranslationsChange}
                        onSelectAll={onSelectAllRetranslations}
                        onDeselectAll={onDeselectAllRetranslations}
                        defaultOpen={false}
                      />
                    </Col>
                    <Col span={gridSpan}></Col>
                  </Row>
                </>
              )}
            </>
          ) : (
            <TransferSection
              title="Учетные записи"
              height={!isMobile ? 350 : 354}
              dataSource={accountsData}
              value={accountsTargetKeys}
              onChange={onAccountsChange}
              onSelectAll={onSelectAllAccounts}
              onDeselectAll={onDeselectAllAccounts}
              showMessagesBlock={true}
              disableCollapse={true}
              loadMessages={loadMessagesFull}
              onLoadMessagesChange={onLoadMessagesFullChange}
              messagePeriod={messagePeriodFull}
              onMessagePeriodChange={onMessagePeriodFullChange}
            />
          )}
        </Flex>
      </Spin>
    </>
  )
}
